import { useTranslation } from 'react-i18next';
import { ExtraWorkOrderLine, ExtraWorkOrder } from '../../../List/models';
import { Product } from '@shared/components/ui/Product';
import { Button } from '@shared/components/ui/button';
import { Printer } from 'lucide-react';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faClock,
  faMapLocationDot,
  faTag,
  faCircleMinus,
  faChevronDown,
  faChevronUp,
  faFolder,
} from '@fortawesome/pro-light-svg-icons';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useUsers } from '../../hooks/useUsers';
import { getExtraWorkOrderStatusConfig } from '../../../List/config/extraWorkOrderStatusConfig';
import { format } from 'date-fns';
import { TooltipProvider, TooltipSH, TooltipTrigger, TooltipContent } from '@shared/components/ui/shadcn/tooltip';
import { EvidenceImageModal } from './EvidenceImageModal';

interface GroupsListProps {
  lines: ExtraWorkOrderLine[];
  order?: ExtraWorkOrder | null;
  onLineClick?: (lineId: number) => void;
  onDispatch?: (locationId: number) => void;
  isDispatching?: boolean;
}

/**
 * Listado de grupos - Muestra los grupos de trabajo con su estado
 *
 * Estilo similar a "Productos Recibidos" con bordes redondeados, sin fondo,
 * título de lista, y datos adicionales con iconos debajo del producto
 */
export const GroupsList = ({ lines, onLineClick, onDispatch, isDispatching }: GroupsListProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const [expandedWastes, setExpandedWastes] = useState<Set<number>>(new Set());
  const [selectedEvidence, setSelectedEvidence] = useState<{ url: string; fileName: string } | null>(null);
  const { getUserName } = useUsers();
  const [isPrinting, setIsPrinting] = useState<number | null>(null);

  // 🎯 Obtener IDs únicos de productos de todas las líneas
  const productIds = useMemo(() => {
    const ids = lines.map((line) => line.product_id).filter((id) => id > 0);
    return Array.from(new Set(ids));
  }, [lines]);

  // 🎯 Consultar productos desde la API
  const { data: productsData } = useQuery({
    queryKey: ['products-for-groups', productIds],
    queryFn: async () => {
      if (productIds.length === 0) return [];

      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.get<any>('/products', {
        fields: 'basic',
        product_ids: productIds.join(','),
        per_page: 1000,
      });

      return response.data || [];
    },
    enabled: productIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // 🎯 Crear mapa de productos por ID
  const productsMap = useMemo(() => {
    if (!productsData) return new Map();
    return new Map(productsData.map((p: any) => [p.id, p]));
  }, [productsData]);

  // 🎯 Calcular progreso
  const getProgress = (line: ExtraWorkOrderLine) => {
    return { done: line.qty_done, total: line.qty_expected };
  };

  // 🎯 Toggle de expansión de mermas
  const toggleWastes = (lineId: number) => {
    setExpandedWastes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(lineId)) newSet.delete(lineId);
      else newSet.add(lineId);
      return newSet;
    });
  };

  // 🎯 Manejar impresión
  const handlePrint = async (line: ExtraWorkOrderLine) => {
    try {
      setIsPrinting(line.id);
      const dateStr = line.created_at ? line.created_at.replace(' ', 'T') : new Date().toISOString();
      const dateObj = new Date(dateStr);

      const labelUrl = await generateReusedLabel({
        code: line.group_code,
        date: isNaN(dateObj.getTime()) ? '' : format(dateObj, 'dd/MM/yyyy'),
      });
      window.open(labelUrl, '_blank');
    } catch (error) {
      console.error('Error printing group label:', error);
    } finally {
      setIsPrinting(null);
    }
  };

  // 🎯 Renderizar un grupo
  const renderGroup = (line: ExtraWorkOrderLine) => {
    const product = productsMap.get(line.product_id) || line.product;
    const statusConfig = getExtraWorkOrderStatusConfig(line.status);
    const progress = getProgress(line);
    const isComplete = line.status === 'DONE';
    const productNotFound = !product;
    const hasWastes = line.wastes && line.wastes.length > 0;
    const isWastesExpanded = expandedWastes.has(line.id);
    const canDispatch = line.status === 'IN_PROGRESS' && line.done_location_id !== null;

    // Formatear fechas
    const formattedCreatedAt = line.created_at ? format(new Date(line.created_at), 'dd/MM/yyyy HH:mm') : null;
    const formattedStartedAt = line.started_at ? format(new Date(line.started_at), 'dd/MM/yyyy HH:mm') : null;

    // Calcular porcentaje de progreso
    const progressPercentage = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
      <div
        key={line.id}
        onClick={() => {
          if (hasWastes) toggleWastes(line.id);
          onLineClick?.(line.id);
        }}
        className={`rounded-lg border border-card-border overflow-hidden transition-colors ${
          hasWastes ? 'hover:bg-card-bg cursor-pointer' : ''
        }`}
        data-testid={`group-item-${line.id}`}
      >
        <div className="w-full h-1 relative">
          <div
            className={`h-full ${isComplete ? 'bg-success' : 'bg-info'}`}
            style={{ width: isComplete ? '100%' : `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Columna izquierda: Producto y datos */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-medium text-text">{line.group_code}</p>
                {/* Badge de estado */}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                >
                  {t(`lineStatus.${line.status}`, statusConfig.label)}
                </span>
              </div>

              <div className="mb-3">
                <Product
                  id={product?.id || line.product_id}
                  src={line.product?.image_hash || product?.image_hash || product?.image || ''}
                  name={product?.name || t('detail.lines.product_not_found', 'Producto no encontrado')}
                  code={productNotFound ? String(line.product_id) : product?.code || ''}
                  acronym={productNotFound ? undefined : product ? undefined : 'PN'}
                  enableLineClamp={false}
                  showTooltip={false}
                />
              </div>

              <TooltipProvider>
                <div className="flex flex-wrap items-center gap-4 text-xs text-complementary-text">
                  {(formattedCreatedAt || formattedStartedAt) && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help">
                          <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                          <span>{formattedStartedAt || formattedCreatedAt}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>
                          {formattedStartedAt
                            ? t('detail.header.started_at', 'Iniciada')
                            : t('detail.lines.created_at', 'Fecha creación')}
                        </p>
                      </TooltipContent>
                    </TooltipSH>
                  )}

                  <TooltipSH>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-help">
                        <FontAwesomeIcon icon={faTag} className="w-3 h-3" />
                        <span>{t(`workTypes.${line.work_type}`, line.work_type)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-body-bg border-card-border">
                      <p>{t('detail.lines.work_type', 'Tipo trabajo')}</p>
                    </TooltipContent>
                  </TooltipSH>

                  {line.done_location && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help">
                          <FontAwesomeIcon icon={faMapLocationDot} className="w-3 h-3" />
                          <span>{line.done_location.code || line.done_location.name || '-'}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>{t('detail.lines.done_location', 'Ubicación destino')}</p>
                      </TooltipContent>
                    </TooltipSH>
                  )}

                  {line.qty_waste > 0 && (
                    <TooltipSH>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help bg-bg-warning text-text px-2 py-0.5 rounded">
                          <FontAwesomeIcon icon={faCircleMinus} className="w-3 h-3" />
                          <span>{line.qty_waste}</span>
                          {hasWastes && (
                            <FontAwesomeIcon
                              icon={isWastesExpanded ? faChevronUp : faChevronDown}
                              className="w-2.5 h-2.5"
                            />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-body-bg border-card-border">
                        <p>{t('detail.lines.qty_waste', 'Cantidad merma')}</p>
                      </TooltipContent>
                    </TooltipSH>
                  )}
                </div>
              </TooltipProvider>
            </div>

            {/* Columna derecha: Progreso y Acciones */}
            <div className="flex flex-col items-end gap-3 flex-shrink-0">
              <div className="flex flex-col items-end gap-1">
                {isComplete && <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-success" />}
                <span className={`text-sm font-medium ${isComplete ? 'text-success' : 'text-text'}`}>
                  {progress.done}/{progress.total}
                </span>
              </div>

              {/* Botones de acción */}
              <div className="flex flex-col gap-2 scale-90 origin-right">
                {/* Botón de Despacho Granular */}
                {canDispatch && (
                  <Button
                    size="sm"
                    variant="primary"
                    className="h-9 shadow-lg shadow-primary/20 flex-shrink-0"
                    loading={isDispatching}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (line.done_location_id !== null) {
                        onDispatch?.(line.done_location_id);
                      }
                    }}
                  >
                    <span className="text-xs font-semibold px-2">{t('actions.dispatch', 'Despachar')}</span>
                  </Button>
                )}

                {/* Botón de imprimir */}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 border-card-border hover:bg-card-bg flex items-center gap-2 group"
                  disabled={isPrinting === line.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrint(line);
                  }}
                  title={t('groups.print_label', 'Imprimir Etiqueta')}
                >
                  {isPrinting === line.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto"></div>
                  ) : (
                    <>
                      <Printer className="w-4 h-4 text-icons group-hover:text-primary transition-colors" />
                      <span className="text-xs font-medium pr-1">{t('groups.print', 'Etiqueta')}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {hasWastes && isWastesExpanded && (
          <div className="px-4 pb-4">
            <div className="space-y-3">
              {line.wastes!.map((waste, index) => (
                <div key={waste.id} className="bg-card-bg border border-card-border rounded-lg p-4">
                  {/* Título: Merma número */}
                  <div className="mb-3">
                    <h5 className="text-base font-semibold mb-2 text-title">
                      {t('detail.wastes.title_singular', 'Merma')} {index + 1}
                    </h5>
                    <div className="border-t border-lines"></div>
                  </div>

                  <div className="text-sm mb-3 text-text">
                    <span>
                      <span className="text-xs text-complementary-text">{t('detail.wastes.qty', 'Cantidad')}</span>{' '}
                      <span className="font-medium">{waste.qty}</span>
                    </span>
                    {waste.created_at && (
                      <>
                        <span className="mx-2 text-complementary-text">•</span>
                        <span>
                          <span className="text-xs text-complementary-text">
                            {t('detail.wastes.created_at', 'Fecha')}
                          </span>{' '}
                          <span>{format(new Date(waste.created_at), 'dd/MM/yyyy HH:mm')}</span>
                        </span>
                      </>
                    )}
                    <span className="mx-2 text-complementary-text">•</span>
                    <span>
                      <span className="text-xs text-complementary-text">
                        {t('detail.wastes.created_by', 'Registrado por')}
                      </span>{' '}
                      <span>{getUserName(waste.created_by)}</span>
                    </span>
                  </div>

                  {waste.evidences && waste.evidences.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <FontAwesomeIcon icon={faFolder} className="w-3 h-3 text-icons" />
                        {waste.evidences.map((evidence: any, idx: number) => {
                          const filePath =
                            typeof evidence === 'string' ? evidence : evidence.file_path || evidence.file_name || '';
                          const fileName =
                            typeof evidence === 'string'
                              ? evidence.split('/').pop() || `evidencia_${idx + 1}`
                              : evidence.file_name || evidence.file_path?.split('/').pop() || `evidencia_${idx + 1}`;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className="text-sm text-info hover:underline cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEvidence({ url: filePath, fileName });
                              }}
                            >
                              {fileName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Separador */}
                  {waste.description && <div className="border-t border-lines mb-3"></div>}

                  {/* Descripción */}
                  {waste.description && (
                    <div>
                      <p className="text-sm text-text">{waste.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (lines.length === 0) {
    return (
      <div className="bg-card-bg border border-card-border rounded-lg p-6">
        <p className="text-sm text-subtext text-center">{t('detail.groups.empty', 'No hay grupos disponibles')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-title">{t('detail.groups.title', 'Grupos de Trabajo')}</h3>
        <span className="text-sm text-subtext">
          {lines.length} {lines.length === 1 ? t('detail.groups.line', 'línea') : t('detail.groups.lines', 'líneas')}
        </span>
      </div>

      {/* Lista de grupos - Con bordes redondeados, sin fondo */}
      <div className="space-y-3">{lines.map(renderGroup)}</div>

      {/* Modal de evidencia */}
      <EvidenceImageModal
        isOpen={!!selectedEvidence}
        onClose={() => setSelectedEvidence(null)}
        imageUrl={selectedEvidence?.url || ''}
        fileName={selectedEvidence?.fileName}
      />
    </div>
  );
};
